import axios from "axios"
import sha256 from "crypto-js/sha256"
import hmacSHA512 from "crypto-js/hmac-sha512"
import Base64 from "crypto-js/enc-base64"
import { safeObject } from "@lib/object"
import { isServer } from "@lib/isomorphic"
import { config } from "@lib/config"

import { BaseProvider } from "./base.provider"

import type { Context } from "./types"

interface Query {
  uri: string
  method?: "GET" | "POST" | "DELETE" | "PUT" | "PATCH"
  data?: object
  formData?: FormData
  query?: object
  headers?: Record<string, string>
  isFormData?: boolean
  isMultiPartData?: boolean
  isFullResponse?: boolean
  isHmacAuthorization?: boolean
  api?: "strapi" | "pure"
  userAgent?: "Pure"
}

export class BaseHttpProvider extends BaseProvider {
  serverTimeDelay: number = 0
  GEO_API_URL = "https://geoiplookup.thepure.team/geoip2lookup"

  baseHost = () => (isServer() ? config.data.innerApiUri : config.data.apiUri)

  pureApiHost = () => {
    if (!isServer() && window.location.hostname.includes("testing")) {
      return "https://testing-api.soulplatform.com"
    } else {
      return config.data.pureApiUrl
    }
  }

  // TODO hardcode - refactor
  pureApiUserAgent = `PureFTP/1.8.20 (JS 1.0; Chrome 99.0.4844.74; ru) SoulSDK/0.14.8 (JS)`
  pureMixedBundlesUserAgent = `Pure/1.8.20 (JS 1.0; Chrome 99.0.4844.74; ru) SoulSDK/0.14.8 (JS)`

  getClientCountryCode = async ({
    req,
  }: {
    req: Partial<Context["req"]> & Required<Pick<Context["req"], "headers">>
  }): Promise<string | undefined> => {
    // Germany by default when development
    if (process.env.NODE_ENV === "development") {
      return "de"
    }
    try {
      const headers =
        req.headers["x-client-ip"] ||
        req.headers["x-forwarded-for"] ||
        req.headers["cf-connecting-ip"] ||
        req.headers["fastly-client-ip"] ||
        req.headers["true-client-ip"] ||
        req.headers["x-real-ip"] ||
        req.headers["x-cluster-client-ip"] ||
        req.headers["x-forwarded"] ||
        req.headers["forwarded-for"] ||
        req.headers["forwarded"] ||
        req.headers["client-ip"] ||
        req.headers["appengine-user-ip"] ||
        req.socket?.remoteAddress
      let resolvedIp = typeof headers === "string" ? headers.split(", ")[0] : ""

      const url = new URL(this.GEO_API_URL)

      if (resolvedIp) {
        url.searchParams.set("ip", resolvedIp)
      }

      // Fetch because of axios doesn't work in the next middleware
      const res = await fetch(url.toString())
      const { country_code } = await res.json()

      return country_code.toLowerCase()
    } catch (e) {
      console.error(e) // eslint-disable-line
    }
  }

  async execute(
    {
      uri,
      method = "GET",
      data = {},
      formData = undefined,
      query = {},
      headers = {},
      isFormData = false,
      isMultiPartData = false,
      isFullResponse = false,
      isHmacAuthorization = false,
      api = "strapi",
      userAgent,
    }: Query,
    host = api === "strapi" ? this.baseHost() : this.pureApiHost(),
  ) {
    const contentType =
      headers["Content-Type"] ||
      (isFormData
        ? "application/x-www-form-urlencoded"
        : isMultiPartData
        ? "multipart/form-data"
        : "application/json")

    let authorization: string | undefined = undefined
    let forwardedFor: string | undefined = undefined
    let resolveData = isMultiPartData || isFormData ? formData : data

    if (isHmacAuthorization && !isMultiPartData && !isFormData) {
      const resolveUri = uri[0] === "/" ? uri : `/${uri}`
      const body = JSON.stringify(resolveData) || null
      const template = `${method}+${body}+42`
      const hashDigest = sha256(template)
      const hmacDigest = Base64.stringify(
        hmacSHA512(hashDigest + resolveUri, config.data.hmacKey),
      )

      authorization = hmacDigest
    }

    const prepareRequest = safeObject({
      method,
      url: `${host}${uri}`,
      data:
        method === "POST" || method === "PUT" || method === "PATCH"
          ? resolveData
          : null,
      responseType: "json",
      params: Object.keys(query).length > 0 ? query : null,
      headers: safeObject({
        "Content-Type": contentType,
        "X-Authorization": authorization,
        "X-Forwarded-For": forwardedFor,
        "x-js-user-agent": api === "pure" ? this.pureApiUserAgent : undefined,
        ...(api === "pure" &&
          userAgent === "Pure" && {
            "x-js-user-agent": this.pureMixedBundlesUserAgent,
          }),
        ...headers,
      }),
    })

    const res = await axios(prepareRequest)

    return isFullResponse ? res : res.data
  }
}
